import { Collapse, createStyles, makeStyles, Theme } from '@material-ui/core';
import MuiCreateIcon from '@material-ui/icons/Create';
import classNames from 'classnames';
import React, { useEffect } from 'react';

import {
  CardType,
  FullAnswerFragment,
  GetAnswersSubscriptionSubscription,
  GetAnswersSubscriptionSubscriptionVariables,
  GetQuestionDetailsQuery,
  QuestionDetailsFragment,
  ReactionType,
  useAnalyticsQuestionClickMutation,
  useGetQuestionDetailsQuery,
  useSeenUserNotificationsMutation,
} from '../../../../graphql/gen/types';
import { GET_ANSWERS_SUBSCRIPTION } from '../../../../graphql/subscriptions/GetAnswers';
import { colorPalette, spacings, TextSizesEnum } from '../../../../theme';
import { MemoizeLastNonNull } from '../../../shared/MemoizeLastNonNull';
import { ButtonColor } from '../../../shared/primitives/PlexButton';
import { PlexIconButton } from '../../../shared/primitives/PlexIconButton';
import { PlexTypography } from '../../../shared/primitives/PlexTypography';
import { useDialogState } from '../../dialog/useDialogState';
import { CardLabels } from '../../feed/CardLabels';
import { QuestionHeader } from '../../feed/QuestionHeader';
import { canEditQuestion } from '../../permissions';
import { Error as ErrorView } from '../../shared/Error';
import { DetailsTopBar } from '../DetailsTopBar';
import { HalfHalfView } from '../HalfHalfView';
import { useCloseOnDelete } from '../useCloseOnDelete';
import { QuestionAnswer } from './Answer';
import { PlexQuestionActions } from './PlexQuestionActions';
import { QuestionAnswers } from './QuestionAnswers';
import { QuestionComments } from './QuestionComments';

type Props = {
  plexQuestion: {
    permalink: string;
    commentsVisible: boolean;
  };
  onChangeCommentsVisibility: (visible: boolean) => void;
};

export function PlexQuestionDetails(props: Props): React.ReactElement {
  const { plexQuestion, onChangeCommentsVisibility } = props;
  const { permalink, commentsVisible } = plexQuestion;
  const [, setDialogState] = useDialogState();
  const { data, loading, error, subscribeToMore } = useGetQuestionDetailsQuery({
    variables: { questionId: permalink },
  });
  const [markQuestionAsSeen] = useSeenUserNotificationsMutation();
  const classes = useStyles();
  useCloseOnDelete({ loading, deleted: data?.loggedIn?.questionById?.deleted || false });

  const handleEdit = (): void => {
    const question = data?.loggedIn?.questionById;
    if (question != null) {
      const dialogType = question.type === CardType.Faq ? 'edit_fact' : 'edit_card';
      setDialogState({ type: dialogType, factId: question.id });
    }
  };
  const plexQuestionData: QuestionDetailsFragment | null | undefined = data?.loggedIn?.questionById;

  useEffect(() => {
    if (data?.loggedIn?.questionById != null) {
      const unsubscribe = subscribeToMore<
        GetAnswersSubscriptionSubscription,
        GetAnswersSubscriptionSubscriptionVariables
      >({
        document: GET_ANSWERS_SUBSCRIPTION,
        variables: { questionId: data.loggedIn.questionById.id },
        updateQuery: (prev: GetQuestionDetailsQuery, { subscriptionData }): GetQuestionDetailsQuery => {
          const answer: FullAnswerFragment | null | undefined = subscriptionData.data.answer;
          if (!prev.loggedIn || !prev.loggedIn.questionById || !answer) {
            return prev;
          }

          return {
            ...prev,
            loggedIn: {
              ...prev.loggedIn,
              questionById: {
                ...prev.loggedIn.questionById,
                answers: [
                  answer,
                  ...prev.loggedIn.questionById.answers.filter((item: FullAnswerFragment) => item.id !== answer?.id),
                ],
              },
            },
          };
        },
      });

      return unsubscribe;
    } else {
      return undefined;
    }
  }, [data, subscribeToMore]);

  useEffect(() => {
    if (data?.loggedIn?.questionById != null && !data.loggedIn.questionById.notification.isRead) {
      markQuestionAsSeen({ variables: { postsIds: [], faqsIds: [data.loggedIn.questionById.id] } });
    }
  }, [data, markQuestionAsSeen]);

  const [analyticsQuestionClick] = useAnalyticsQuestionClickMutation();
  useEffect(() => {
    if (
      data?.loggedIn?.questionById != null &&
      !data.loggedIn.questionById.currentUserReactions.includes(ReactionType.AnalyticsClick)
    ) {
      analyticsQuestionClick({ variables: { questionId: data.loggedIn.questionById.id } });
    }
  }, [analyticsQuestionClick, data]);
  if (loading) {
    return <DetailsTopBar loading />;
  }

  if (error) {
    return <ErrorView error={error} />;
  }

  if (plexQuestionData == null) {
    // We are not loading and we did not get an error.
    // This should be illegal state.
    // TODO: redirect to different URL if this occurs
    throw new Error(`Illegal state: ${error} ${loading} ${data}`);
  }

  const { text } = plexQuestionData;
  const acceptedAnswer: FullAnswerFragment | null =
    plexQuestionData.acceptedAnswer != null && !plexQuestionData.acceptedAnswer.deleted
      ? plexQuestionData.acceptedAnswer
      : null;
  const plexRelation = plexQuestionData.plex.currentUserRelation.state;
  const questionRelation = plexQuestionData.currentUserRelation;

  return (
    <div className={classes.root}>
      <DetailsTopBar>
        <QuestionHeader
          plexName={plexQuestionData.plex.name}
          plexColor={plexQuestionData.plex.color}
          publishStatus={plexQuestionData.status}
          publishedDate={plexQuestionData.createdAt}
          authorName={plexQuestionData.author?.name || 'Root'}
          authorAvatar={plexQuestionData.author?.picture}
          authorTitle={plexQuestionData.author?.jobTitle}
          showUnreadBadge={false}
          hasAnswer={!!plexQuestionData.acceptedAnswer}
        />
      </DetailsTopBar>
      <HalfHalfView
        bottomCollapsed={!commentsVisible}
        topHalfView={
          <div className={classNames(classes.contentContainer)}>
            <div className={classes.contentData}>
              <div className={classes.contentHeader}>
                <div className={classes.titleContainer}>
                  <PlexTypography className={classes.title} text={text} size={TextSizesEnum.XXLarge} />
                  <CardLabels labels={plexQuestionData.labels} />
                </div>
                {canEditQuestion({ plexRelation, questionRelation }) && (
                  <PlexIconButton
                    className={classes.editButton}
                    onClick={handleEdit}
                    MuiIcon={MuiCreateIcon}
                    color={ButtonColor.Purple}
                    tooltip={'Edit Question'}
                  />
                )}
              </div>
              <Collapse className={classes.acceptedAnswer} in={acceptedAnswer != null}>
                <MemoizeLastNonNull>
                  {!!acceptedAnswer && <QuestionAnswer question={plexQuestionData} answer={acceptedAnswer} />}
                </MemoizeLastNonNull>
              </Collapse>
              <QuestionAnswers question={plexQuestionData} />
            </div>
            <PlexQuestionActions plexQuestionData={plexQuestionData} />
          </div>
        }
        bottomHalfView={
          <QuestionComments
            question={plexQuestionData}
            commentsVisible={commentsVisible}
            onChangeCommentsVisibility={onChangeCommentsVisibility}
          />
        }
      />
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      height: '100%',
    },
    answersContainer: {
      display: 'flex',
      flexDirection: 'column',
      overflow: 'auto',
      width: '100%',
    },
    contentHeader: {
      display: 'inline-flex',
      marginTop: spacings.xlarge,
      marginBottom: spacings.xlarge,
      marginLeft: spacings.xxlarge,
      marginRight: spacings.xxlarge,
    },
    title: {
      display: 'inline',
      fontWeight: 'bold',
      color: colorPalette.dark._900,
    },
    titleContainer: {
      display: 'inline-block',
    },
    acceptedAnswer: {
      minHeight: 'unset !important',
    },
    questionLikeRoot: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
    contentContainer: {
      display: 'flex',
      position: 'relative',
      flexDirection: 'row',
      justifyContent: 'space-between',
      width: '100%',
      height: '100%',
      overflow: 'hidden',
    },
    contentData: {
      display: 'flex',
      flexDirection: 'column',
      paddingRight: spacings.xxlarge * 2,
      paddingBottom: spacings.xlarge,
      overflow: 'auto',
      flex: 1,
      width: '100%',
    },
    editButton: {
      display: 'inline', // needed for the edit button to be displayed at the end of the text
      marginLeft: spacings.small,
    },
  })
);
